import NameAndPAN from '../../components/organisms/Tables/CellRenderer/NameAndPAN';
import CurrencyAmount from '../../components/organisms/Tables/CellRenderer/CurrencyAmount';
import DealNameAndStatus from '../../components/organisms/Tables/CellRenderer/DealNameAndStatus';

import * as R from 'ramda';
import { numDifferentiation, twoDecimalNumber } from '../../utils/numbers';
import { getInvestmentStatusTag } from '../../pages/utils/investment';
import DealStatus from '../../components/organisms/Tables/CellRenderer/DealStatus';

function createIDData(
  dealName,
  clientName,
  investedDate,
  investedAmount,
  payoutOn,
  dealStatus,
  payoutAmount,
  irr,
  earnedIncome,
) {
  return {
    dealName,
    clientName,
    investedDate,
    investedAmount,
    payoutOn,
    dealStatus,
    payoutAmount,
    irr,
    earnedIncome,
  };
}

export const getIDRowsAndCols = (data) => {
  const rows = [];
  const data_row = R.path(['data', 'content'], data);
  const today = new Date();

  data_row?.forEach((element) => {
    if (element) {
      const investedDateFormatted = element?.idealTransactionDatetime
        ? new Date(element?.idealTransactionDatetime).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';

      const payoutDateFormatted = element?.maturityOn
        ? new Date(element.maturityOn).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';

      const payoutDate = new Date(element.maturityOn);

      const dateDifference = Math.ceil((payoutDate - today) / (1000 * 60 * 60 * 24));

      const status = element?.transactionStatus;

      rows.push(
        createIDData(
          <DealNameAndStatus
            name={element?.brandName ? element?.brandName : element?.companyName}
            status={element?.transactionStatus}
            isReinvestInvestment={element?.reinvestStatus}
            dealData={element}
          />,
          <NameAndPAN name={element?.clientName} pan={element?.clientPan} />,
          investedDateFormatted,
          <div className="flex items-center gap-1">
            <CurrencyAmount amount={element?.transactionAmount} />
            {getInvestmentStatusTag(element?.state)}
          </div>,
          payoutDateFormatted,
          <DealStatus noOfDays={dateDifference} status={status} />,
          <CurrencyAmount amount={element?.idealPayoutAmount} />,
          `${element.irr ? twoDecimalNumber(element.irr) + '%' : '-'}`,
          numDifferentiation(element?.earnedIncome),
        ),
      );
    }
  });

  return rows;
};

function createBondsData(
  dealName,
  clientName,
  investedAmount,
  ytm,
  settlementOn,
  unit,
  dealStatus,
  maturityOn,
  earnedIncome,
) {
  return {
    dealName,
    clientName,
    investedAmount,
    ytm,
    settlementOn,
    unit,
    dealStatus,
    maturityOn,
    earnedIncome,
  };
}

export const getBondsRowsAndCols = (data) => {
  const rows = [];
  const data_row = R.path(['data', 'content'], data);
  const today = new Date();
  data_row?.forEach((element) => {
    if (element) {
      const settlementDateFormatted = element?.settlementOn
        ? new Date(element.settlementOn).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';

      const maturityDateFormatted = element?.maturityOn
        ? new Date(element?.maturityOn).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';
      const payoutDate = new Date(element.maturityOn);

      const dateDifference = Math.ceil((payoutDate - today) / (1000 * 60 * 60 * 24));
      rows.push(
        createBondsData(
          <DealNameAndStatus
            name={element?.companyName}
            status={element?.transactionStatus}
            isReinvestInvestment={element?.reinvestStatus}
            dealData={element}
          />,
          <NameAndPAN name={element?.clientName} pan={element?.clientPan} />,
          <CurrencyAmount amount={element?.transactionAmount} />,
          `${element.ytm ? twoDecimalNumber(element.ytm) + '%' : '-'}`,
          settlementDateFormatted,
          `${element.unit ? twoDecimalNumber(element.unit) : '-'}`,
          <DealStatus noOfDays={dateDifference} />,
          maturityDateFormatted,
          numDifferentiation(element?.earnedIncome),
        ),
      );
    }
  });

  return rows;
};

function createGoldData(
  dealName,
  clientName,
  investedDate,
  investedAmount,
  payoutOn,
  payoutAmount,
  irr,
  earnedIncome,
) {
  return {
    dealName,
    clientName,
    investedDate,
    investedAmount,
    payoutOn,
    payoutAmount,
    irr,
    earnedIncome,
  };
}

export const getGoldRowsAndCols = (data) => {
  const rows = [];
  const data_row = R.path(['data', 'content'], data);

  data_row?.forEach((element) => {
    if (element) {
      const investedDateFormatted = element?.idealTransactionDatetime
        ? new Date(element?.idealTransactionDatetime).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';

      const payoutDateFormatted = element?.maturityOn
        ? new Date(element.maturityOn).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';

      rows.push(
        createGoldData(
          <DealNameAndStatus
            name={element?.companyName}
            status={element?.transactionStatus}
            isReinvestInvestment={element?.reinvestStatus}
            dealData={element}
          />,
          <NameAndPAN name={element?.clientName} pan={element?.clientPan} />,
          investedDateFormatted,
          <div className="flex items-center gap-1">
            <CurrencyAmount amount={element?.transactionAmount} />
            {getInvestmentStatusTag(element?.state)}
          </div>,
          payoutDateFormatted,
          <CurrencyAmount amount={element?.idealPayoutAmount} />,
          `${element.irr ? twoDecimalNumber(element.irr) + '%' : '-'}`,
          numDifferentiation(element?.earnedIncome),
        ),
      );
    }
  });

  return rows;
};

function createData(
  dealName,
  clientName,
  investedDate,
  investedAmount,
  dealStatus,
  payoutOn,
  payoutAmount,
  irr,
  earnedIncome,
) {
  return {
    dealName,
    clientName,
    investedDate,
    investedAmount,
    dealStatus,
    payoutOn,
    payoutAmount,
    irr,
    earnedIncome,
  };
}

export const getRowsAndCols = (data) => {
  const rows = [];
  const data_row = R.path(['data', 'content'], data);
  const today = new Date();
  data_row?.forEach((element) => {
    if (element) {
      const investedDateFormatted = element?.idealTransactionDatetime
        ? new Date(element?.idealTransactionDatetime).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';

      const payoutDateFormatted = element?.maturityOn
        ? new Date(element.maturityOn).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '-';
      const payoutDate = new Date(element.maturityOn);

      const dateDifference = Math.ceil((payoutDate - today) / (1000 * 60 * 60 * 24));
      rows.push(
        createData(
          <DealNameAndStatus
            name={element?.companyName}
            status={element?.transactionStatus}
            isReinvestInvestment={element?.reinvestStatus}
            dealData={element}
          />,
          <NameAndPAN name={element?.clientName} pan={element?.clientPan} />,
          investedDateFormatted,
          <div className="flex items-center gap-1">
            <CurrencyAmount amount={element?.transactionAmount} />
            {getInvestmentStatusTag(element?.state)}
          </div>,
          <DealStatus noOfDays={dateDifference} />,
          payoutDateFormatted,
          <CurrencyAmount amount={element?.idealPayoutAmount} />,
          `${element.irr ? twoDecimalNumber(element.irr) + '%' : '-'}`,
          numDifferentiation(element?.earnedIncome),
        ),
      );
    }
  });

  return rows;
};
